import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useTranslation } from "react-i18next";
import { Wallet, ChevronDown, ChevronUp, Lock } from "lucide-react";
import { useToast } from "./ui/use-toast";
import * as paymentService from "../services/paymentService";
import type { CryptoWalletSettings } from "../types/api";

export type PaymentMethod = "card" | "crypto";

export interface PaymentFormData {
  method: PaymentMethod;
  // Carte
  cardNumber: string;
  expiryDate: string;
  cvv: string;
  cardholderName: string;
  billingAddress: string;
  apartment: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  // Crypto
  walletAddress: string;
  cryptoAsset: "USDT" | "BTC" | "ETH" | "BNB";
  cryptoNetwork: "TRC20" | "ERC20" | "BTC" | "ETH" | "BSC";
}

interface PaymentFormModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: PaymentFormData) => Promise<void>;
  amount: number;
  packName: string;
  isProcessing?: boolean;
}

const PaymentFormModal = ({
  open,
  onClose,
  onConfirm,
  amount,
  packName,
  isProcessing = false,
}: PaymentFormModalProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<PaymentMethod>("card");
  const [cardExpanded, setCardExpanded] = useState(true);
  const [cryptoExpanded, setCryptoExpanded] = useState(false);
  const [formData, setFormData] = useState<PaymentFormData>({
    method: "card",
    cardNumber: "",
    expiryDate: "",
    cvv: "",
    cardholderName: "",
    billingAddress: "",
    apartment: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
    walletAddress: "",
    cryptoAsset: "USDT",
    cryptoNetwork: "TRC20",
  });
  const [cryptoWallets, setCryptoWallets] =
    useState<CryptoWalletSettings | null>(null);
  const [loadingWallets, setLoadingWallets] = useState(false);

  // Détection automatique du pays basée sur la locale du navigateur
  useEffect(() => {
    const detectCountry = async () => {
      try {
        // Méthode 1: Utiliser l'API Intl pour obtenir la locale
        const locale = Intl.DateTimeFormat().resolvedOptions().locale;
        const countryFromLocale = locale.split("-")[1]?.toUpperCase();

        if (countryFromLocale && countryFromLocale.length === 2) {
          setFormData((prev) => ({
            ...prev,
            country: countryFromLocale,
          }));
          return;
        }

        // Méthode 2: Utiliser navigator.language
        const browserLang =
          navigator.language || (navigator as any).userLanguage;
        const countryFromLang = browserLang.split("-")[1]?.toUpperCase();

        if (countryFromLang && countryFromLang.length === 2) {
          setFormData((prev) => ({
            ...prev,
            country: countryFromLang,
          }));
          return;
        }

        // Méthode 3: Essayer de détecter via l'API de géolocalisation IP (fallback)
        try {
          const response = await fetch("https://ipapi.co/json/", {
            method: "GET",
            headers: { Accept: "application/json" },
          });
          const data = await response.json();
          if (data.country_code) {
            setFormData((prev) => ({
              ...prev,
              country: data.country_code.toUpperCase(),
            }));
            return;
          }
        } catch {
          // Ignorer les erreurs de l'API IP
        }

        // Fallback: FR par défaut
        setFormData((prev) => ({
          ...prev,
          country: prev.country || "FR",
        }));
      } catch (error) {
        // En cas d'erreur, utiliser FR par défaut
        setFormData((prev) => ({
          ...prev,
          country: prev.country || "FR",
        }));
      }
    };

    if (open && !formData.country) {
      void detectCountry();
    }
  }, [open, formData.country]);

  // Les champs de carte sont gérés par Stripe
  useEffect(() => {
    if (activeTab !== "crypto") return;
    const asset = formData.cryptoAsset;
    if (asset !== "USDT") {
      setFormData((prev) => ({
        ...prev,
        cryptoNetwork:
          asset === "BTC" ? "BTC" : asset === "BNB" ? "BSC" : "ETH",
      }));
    }
  }, [activeTab, formData.cryptoAsset]);

  useEffect(() => {
    if (!open || activeTab !== "crypto") return;
    let cancelled = false;
    const loadWallets = async () => {
      try {
        setLoadingWallets(true);
        const data = await paymentService.getCryptoWallets();
        if (!cancelled) {
          setCryptoWallets(data);
        }
      } catch {
        if (!cancelled) {
          setCryptoWallets(null);
        }
      } finally {
        if (!cancelled) {
          setLoadingWallets(false);
        }
      }
    };
    void loadWallets();
    return () => {
      cancelled = true;
    };
  }, [open, activeTab]);

  const selectedWallet = useMemo(() => {
    if (!cryptoWallets) return null;
    if (formData.cryptoAsset === "USDT") {
      return formData.cryptoNetwork === "ERC20"
        ? cryptoWallets.usdtErc20
        : cryptoWallets.usdtTrc20;
    }
    if (formData.cryptoAsset === "BTC") return cryptoWallets.btc;
    if (formData.cryptoAsset === "ETH") return cryptoWallets.eth;
    if (formData.cryptoAsset === "BNB") return cryptoWallets.bnb;
    return null;
  }, [cryptoWallets, formData.cryptoAsset, formData.cryptoNetwork]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // S'assurer que formData.method correspond à activeTab
    const currentMethod = activeTab;
    const dataToSubmit = { ...formData, method: currentMethod };

    if (currentMethod === "crypto") {
      if (loadingWallets) {
        toast({
          title: t("payment.form.cryptoWalletMissing", "Adresse indisponible"),
          description: t(
            "payment.form.cryptoWalletLoading",
            "Chargement des adresses en cours, veuillez réessayer.",
          ),
          variant: "destructive",
        });
        return;
      }
      if (!selectedWallet) {
        toast({
          title: t("payment.form.cryptoWalletMissing", "Adresse indisponible"),
          description: t(
            "payment.form.cryptoWalletMissing",
            "L'adresse du réseau choisi n'est pas configurée.",
          ),
          variant: "destructive",
        });
        return;
      }
    }

    // Les informations de carte sont saisies via Stripe au prochain écran

    try {
      await onConfirm(dataToSubmit);
    } catch (error) {
      // L'erreur est gérée par le parent
    }
  };

  const cryptoNetworkLabel =
    formData.cryptoAsset === "BNB"
      ? t("payment.form.cryptoNetworkBnbLabel", "BSC (BEP20)")
      : formData.cryptoAsset === "BTC"
        ? t("payment.form.cryptoNetworkBtcLabel", "BTC (mainnet)")
        : formData.cryptoNetwork;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto border border-border bg-card p-0 text-foreground dark:border-white/10 dark:bg-slate-950/95 dark:text-white">
        <div className="min-h-[600px] bg-background/80 p-8 dark:bg-slate-900/50">
          <div className="flex items-center justify-center gap-2 mb-6">
            <div
              className={`h-2 w-2 rounded-full transition ${
                activeTab === "card"
                  ? "bg-orange-500"
                  : "bg-muted-foreground/30 dark:bg-white/20"
              }`}
            />
            <div
              className={`h-2 w-2 rounded-full transition ${
                activeTab === "crypto"
                  ? "bg-orange-500"
                  : "bg-muted-foreground/30 dark:bg-white/20"
              }`}
            />
          </div>

          <h2 className="mb-8 text-2xl font-bold text-foreground dark:text-white">
            {t("payment.form.chooseMethod", "Choisissez un mode de paiement")}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Section Carte */}
            {/* <div className="border border-white/10 rounded-lg bg-slate-800/50 backdrop-blur-sm">
                <button
                  type="button"
                  onClick={() => {
                    setCardExpanded(!cardExpanded);
                    if (!cardExpanded) {
                      setActiveTab("card");
                      setFormData((prev) => ({ ...prev, method: "card" }));
                      setCryptoExpanded(false);
                    }
                  }}
                  className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-white">
                      {t("payment.form.cardTab", "Carte")}
                    </h3>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-blue-400 ">
                        <img
                          src="https://cart.hostinger.com/assets/payments/processout.svg"
                          alt="VISA"
                          className="w-22 h-8"
                        />
                      </span>
                    </div>
                  </div>
                  {cardExpanded ? (
                    <ChevronUp className="w-5 h-5 text-white/60" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-white/60" />
                  )}
                </button>

                {cardExpanded && (
                  <div className="px-4 pb-6 space-y-4 border-t border-white/10">
                    <div className="pt-4">
                      <p className="text-sm text-white/70">
                        {t(
                          "payment.form.cardSecureNotice",
                          "Les informations de carte sont saisies dans l'étape sécurisée Stripe."
                        )}
                      </p>
                    </div> */}

            {/* Champ pays caché - détection automatique en arrière-plan */}

            {/* <div className="hidden">
                      <Label className="text-sm font-medium text-white/70 mb-2 block">
                        {t("payment.form.billingCountryLabel", "Pays de facturation")}
                      </Label>
                      <Input
                        type="text"
                        value={formData.country}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            country: e.target.value.toUpperCase(),
                          }))
                        }
                        placeholder={t(
                          "payment.form.billingCountryPlaceholder",
                          "FR"
                        )}
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isProcessing}
                      className="w-full bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-500 hover:to-blue-600 text-white py-3 font-semibold">
                      {isProcessing
                        ? t("payment.form.processing", "Traitement...")
                        : t(
                            "payment.form.submitPayment",
                            "Soumettre le paiement"
                          )}
                    </Button>

                    <div className="flex items-center gap-2 text-sm text-white/60">
                      <Lock className="w-4 h-4" />
                      <span>
                        {t(
                          "payment.form.securePayment",
                          "Paiements cryptés et sécurisés"
                        )}
                      </span>
                    </div>

                    <p className="text-xs text-white/50">
                      {t(
                        "payment.form.terms",
                        "En validant votre commande, vous acceptez nos **Conditions d'utilisation** et confirmez avoir lu notre **Politique de confidentialité**. Vous pouvez annuler les paiements récurrents à tout moment."
                      )}
                    </p>
                  </div>
                )}
              </div> */}

            {/* Section Crypto */}
            <div className="rounded-lg border border-border bg-card/70 backdrop-blur-sm dark:border-white/10 dark:bg-slate-800/50">
              <button
                type="button"
                onClick={() => {
                  setCryptoExpanded(!cryptoExpanded);
                  if (!cryptoExpanded) {
                    setActiveTab("crypto");
                    setFormData((prev) => ({ ...prev, method: "crypto" }));
                    setCardExpanded(false);
                  }
                }}
                className="w-full flex items-center justify-between p-4 transition hover:bg-muted/60 dark:hover:bg-white/5">
                <div className="flex items-center gap-3">
                  <Wallet className="w-5 h-5 text-cyan-400" />
                  <h3 className="text-lg font-semibold text-foreground dark:text-white">
                    {t("payment.form.cryptoTab", "Crypto")}
                  </h3>
                </div>
                {cryptoExpanded ? (
                  <ChevronUp className="w-5 h-5 text-muted-foreground dark:text-white/60" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-muted-foreground dark:text-white/60" />
                )}
              </button>

              {cryptoExpanded && (
                <div className="space-y-4 border-t border-border px-4 pb-6 dark:border-white/10">
                  <div className="pt-4">
                    <Label className="mb-2 block text-sm font-medium text-muted-foreground dark:text-white/70">
                      {t("payment.form.cryptoAssetLabel", "Actif")}
                    </Label>
                    <Select
                      value={formData.cryptoAsset}
                      onValueChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          cryptoAsset:
                            value === "BTC" ||
                            value === "ETH" ||
                            value === "BNB"
                              ? value
                              : "USDT",
                        }))
                      }>
                      <SelectTrigger className="border-border bg-background text-foreground dark:border-white/10 dark:bg-white/5 dark:text-white">
                        <SelectValue placeholder="USDT" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USDT">USDT</SelectItem>
                        <SelectItem value="BTC">BTC</SelectItem>
                        <SelectItem value="ETH">ETH</SelectItem>
                        <SelectItem value="BNB">BNB</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="mt-1 text-xs text-muted-foreground dark:text-white/50">
                      {t(
                        "payment.form.cryptoAssetHint",
                        "Choisissez l'actif que vous souhaitez envoyer.",
                      )}
                    </p>
                  </div>
                  <div className="pt-4">
                    <Label className="mb-2 block text-sm font-medium text-muted-foreground dark:text-white/70">
                      {t("payment.form.cryptoNetworkLabel", "Réseau")}
                    </Label>
                    {formData.cryptoAsset === "USDT" ? (
                      <Select
                        value={formData.cryptoNetwork}
                        onValueChange={(value) =>
                          setFormData((prev) => ({
                            ...prev,
                            cryptoNetwork:
                              value === "ERC20" ? "ERC20" : "TRC20",
                          }))
                        }>
                        <SelectTrigger className="border-border bg-background text-foreground dark:border-white/10 dark:bg-white/5 dark:text-white">
                          <SelectValue placeholder="TRC20" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="TRC20">TRC20</SelectItem>
                          <SelectItem value="ERC20">ERC20 (ETH)</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : formData.cryptoAsset === "ETH" ? (
                      <Select
                        value={formData.cryptoNetwork}
                        onValueChange={() =>
                          setFormData((prev) => ({
                            ...prev,
                            cryptoNetwork: "ETH",
                          }))
                        }>
                        <SelectTrigger className="border-border bg-background text-foreground dark:border-white/10 dark:bg-white/5 dark:text-white">
                          <SelectValue placeholder="ERC20 (ETH)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ETH">ERC20 (ETH)</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-foreground/85 dark:border-white/10 dark:bg-white/5 dark:text-white/70">
                        {cryptoNetworkLabel}
                      </div>
                    )}
                    <p className="mt-1 text-xs text-muted-foreground dark:text-white/50">
                      {formData.cryptoAsset === "USDT"
                        ? t(
                            "payment.form.cryptoNetworkHint",
                            "Choisissez TRC20 ou ERC20.",
                          )
                        : formData.cryptoAsset === "ETH"
                          ? t(
                              "payment.form.cryptoNetworkEthHint",
                              "Réseau Ethereum (ERC20).",
                            )
                          : formData.cryptoAsset === "BNB"
                            ? t(
                                "payment.form.cryptoNetworkBnbHint",
                                "Réseau BSC (BEP20).",
                              )
                            : formData.cryptoAsset === "BTC"
                              ? t(
                                  "payment.form.cryptoNetworkBtcHint",
                                  "Réseau Bitcoin (BTC).",
                                )
                              : t(
                                  "payment.form.cryptoNetworkAutoHint",
                                  "Réseau défini automatiquement.",
                                )}
                    </p>
                    <p className="mt-1 text-xs text-amber-300/90">
                      {t(
                        "payment.form.cryptoNetworkWarning",
                        "⚠️ Sélectionnez exactement le même réseau que celui de votre wallet (ex: Binance), sinon les fonds peuvent être perdus.",
                      )}
                    </p>
                  </div>
                  <div className="pt-4">
                    <div className="rounded-lg border border-cyan-500/25 bg-cyan-500/10 p-4">
                      <p className="mb-2 text-sm text-cyan-700 dark:text-cyan-300">
                        {t(
                          "payment.form.cryptoAddressInfoTitle",
                          "Adresse de dépôt",
                        )}
                      </p>
                      <p className="text-xs text-cyan-700/80 dark:text-cyan-300">
                        {t(
                          "payment.form.cryptoAddressInfoDescription",
                          "L'adresse s'affichera après la création du dépôt, selon le réseau choisi.",
                        )}
                      </p>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={isProcessing}
                    className="w-full bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-500 hover:to-blue-600 text-white py-3 font-semibold">
                    {isProcessing
                      ? t("payment.form.processing", "Traitement...")
                      : t(
                          "payment.form.submitPayment",
                          "Soumettre le paiement",
                        )}
                  </Button>
                </div>
              )}
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentFormModal;
